/**
 * Local Database Service
 *
 * HTTP backend in Electron desktop (preload-http.js) or IPC in legacy standalone builds.
 */

import { API_BASE as CONFIGURED_API_BASE } from '../../shared/utils/apiConfig';
import i18n from '../../i18n';

const ld = (key, opts) => i18n.t(`serviceMessages.localDb.${key}`, opts);

const isElectron = () => window.electronAPI !== undefined;

const usesElectronIpc = () =>
  isElectron() && typeof window.electronAPI?.get === 'function';

const API_BASE_URL = (() => {
  if (CONFIGURED_API_BASE) {
    return String(CONFIGURED_API_BASE).replace(/\/api\/?$/, '');
  }
  return 'http://localhost:5001';
})();

/** Legacy entity keys → HTTP API paths (matches localApiAdapter + backend routes). */
const ENTITY_API_PATH = {
  clients: 'facturation/clients',
  documents: 'archives',
  products: 'products',
  categories: 'categories',
  employees: 'hr/employees',
};

function resolveEntityApiPath(entity) {
  return ENTITY_API_PATH[entity] || entity;
}

class LocalDbService {
  async getAll(entity) {
    if (usesElectronIpc()) {
      return window.electronAPI.get(entity);
    }

    const apiPath = resolveEntityApiPath(entity);
    const response = await fetch(`${API_BASE_URL}/api/${apiPath}`);
    if (!response.ok) throw new Error(ld('failed_fetch', { entity }));
    return response.json();
  }

  async get(entity, id) {
    if (usesElectronIpc()) {
      const items = await window.electronAPI.get(entity);
      return items.find((item) => item.id === id);
    }

    const apiPath = resolveEntityApiPath(entity);
    const response = await fetch(`${API_BASE_URL}/api/${apiPath}/${id}`);
    if (!response.ok) throw new Error(ld('failed_fetch_one', { entity, id }));
    return response.json();
  }

  async create(entity, data) {
    if (usesElectronIpc()) {
      return window.electronAPI.post(entity, data);
    }

    const apiPath = resolveEntityApiPath(entity);
    const response = await fetch(`${API_BASE_URL}/api/${apiPath}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(ld('failed_create', { entity }));
    return response.json();
  }

  async update(entity, id, data) {
    if (usesElectronIpc()) {
      return window.electronAPI.put(entity, id, data);
    }

    const apiPath = resolveEntityApiPath(entity);
    const response = await fetch(`${API_BASE_URL}/api/${apiPath}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(ld('failed_update', { entity, id }));
    return response.json();
  }

  async delete(entity, id) {
    if (usesElectronIpc()) {
      return window.electronAPI.delete(entity, id);
    }

    const apiPath = resolveEntityApiPath(entity);
    const response = await fetch(`${API_BASE_URL}/api/${apiPath}/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error(ld('failed_delete', { entity, id }));
    return response.json();
  }

  async getSettings(key) {
    if (usesElectronIpc() && typeof window.electronAPI?.getSettings === 'function') {
      const settings = await window.electronAPI.getSettings();
      return key ? settings[key] : settings;
    }

    const response = await fetch(`${API_BASE_URL}/api/settings`);
    if (!response.ok) throw new Error(ld('failed_fetch_settings'));
    const settings = await response.json();
    return key ? settings[key] : settings;
  }

  async updateSetting(key, value) {
    if (
      usesElectronIpc() &&
      typeof window.electronAPI?.getSettings === 'function' &&
      typeof window.electronAPI?.putSettings === 'function'
    ) {
      const settings = await window.electronAPI.getSettings();
      settings[key] = value;
      return window.electronAPI.putSettings(settings);
    }

    const response = await fetch(`${API_BASE_URL}/api/settings/${key}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value }),
    });
    if (!response.ok) throw new Error(ld('failed_update_setting', { key }));
    return response.json();
  }

  async isAvailable() {
    try {
      await this.getSettings();
      return true;
    } catch (error) {
      console.error('Database not available:', error);
      return false;
    }
  }
}

const localDbService = new LocalDbService();
export default localDbService;
